import { create } from 'zustand';
import { 
  fetchAllFromCloud, 
  addBloodPressureToCloud, 
  addBloodSugarToCloud, 
  deleteRecordFromCloud,
  importDataToCloud,
  getStatsFromCloud,
  getMemoryCache,
  clearMemoryCache
} from '@/services/cloudStore';
import type { HealthRecord, BloodPressureRecord, BloodSugarRecord, MedicalExamIndicator, ParsedLarkData } from '@/types';

interface HealthState {
  // Data（内存缓存，页面刷新会丢失）
  records: HealthRecord[];
  bpRecords: BloodPressureRecord[];
  bsRecords: BloodSugarRecord[];
  
  // Loading states
  isLoading: boolean;
  
  // Actions
  loadRecords: () => Promise<void>;
  addBloodPressure: (data: {
    date: string;
    systolic: number;
    diastolic: number;
    heartRate?: number;
    measureTime?: 'morning' | 'evening';
    notes?: string;
  }) => Promise<void>;
  addBloodSugar: (data: {
    date: string;
    value: number;
    type: 'fasting' | 'post_meal_1h' | 'post_meal_2h' | 'random';
    notes?: string;
  }) => Promise<void>;
  importLarkData: (data: ParsedLarkData[]) => Promise<{ success: number; skipped: number }>;
  addMedicalExamIndicators: (recordId: number, indicators: Omit<MedicalExamIndicator, 'id' | 'recordId'>[]) => Promise<void>;
  deleteRecord: (id: number) => Promise<void>;
  refreshData: () => Promise<void>;
  clearData: () => void;
  
  // Stats
  stats: {
    totalRecords: number;
    bpRecords: number;
    bsRecords: number;
  } | null;
  loadStats: () => Promise<void>;
}

export const useHealthStore = create<HealthState>((set, get) => ({
  records: [],
  bpRecords: [],
  bsRecords: [],
  isLoading: false,
  stats: null,

  // 从云端加载数据到内存
  loadRecords: async () => {
    set({ isLoading: true });
    try {
      const { records, bpRecords, bsRecords } = await fetchAllFromCloud();
      set({ records, bpRecords, bsRecords });
    } finally {
      set({ isLoading: false });
    }
  },

  // 添加血压记录（直接上传云端）
  addBloodPressure: async (data) => {
    set({ isLoading: true });
    try {
      await addBloodPressureToCloud(data);
      // 更新内存中的数据
      const cache = getMemoryCache();
      set({ 
        records: cache.records, 
        bpRecords: cache.bpRecords 
      });
    } finally {
      set({ isLoading: false });
    }
    await get().loadStats();
  },

  // 添加血糖记录（直接上传云端）
  addBloodSugar: async (data) => {
    set({ isLoading: true });
    try {
      await addBloodSugarToCloud(data);
      // 更新内存中的数据
      const cache = getMemoryCache();
      set({ 
        records: cache.records, 
        bsRecords: cache.bsRecords 
      });
    } finally {
      set({ isLoading: false });
    }
    await get().loadStats();
  },

  // 导入飞书数据（批量上传到云端）
  importLarkData: async (dataList) => {
    set({ isLoading: true });
    try {
      const result = await importDataToCloud(dataList.map(d => ({
        date: d.date,
        systolic: d.systolic,
        diastolic: d.diastolic,
        bloodSugar: d.bloodSugar,
        type: 'fasting'
      })));
      
      // 重新加载数据到内存
      const cache = getMemoryCache();
      set({ 
        records: cache.records, 
        bpRecords: cache.bpRecords,
        bsRecords: cache.bsRecords
      });
      
      return result;
    } finally {
      set({ isLoading: false });
    }
  },

  // 体检指标（暂存本地，后续可扩展）
  addMedicalExamIndicators: async (recordId, indicators) => {
    // TODO: 云端化体检指标
    console.log('添加体检指标:', recordId, indicators);
    await get().refreshData();
  },

  // 删除记录（云端软删除）
  deleteRecord: async (id) => {
    set({ isLoading: true });
    try {
      await deleteRecordFromCloud(id);
      // 更新内存
      const cache = getMemoryCache();
      set({ 
        records: cache.records,
        bpRecords: cache.bpRecords,
        bsRecords: cache.bsRecords
      });
    } finally {
      set({ isLoading: false });
    }
    await get().loadStats();
  },

  // 刷新数据（从内存缓存）
  refreshData: async () => {
    const cache = getMemoryCache();
    set({ 
      records: cache.records, 
      bpRecords: cache.bpRecords, 
      bsRecords: cache.bsRecords 
    });
  },

  // 清空数据（退出登录时调用）
  clearData: () => {
    clearMemoryCache();
    set({ records: [], bpRecords: [], bsRecords: [], stats: null });
  },

  // 加载统计信息
  loadStats: async () => {
    const stats = await getStatsFromCloud();
    set({ stats });
  }
}));